import { secure } from '#library/proxy.library.js';
import { isInteger, isFunction, isReference, isMap, isSet } from '#library/assertion.library.js';
/**
 * ## serialize
 * Robust deterministic serialization for cache keys.
 * Handles BigInt, Map, Set, Function, Undefined, and Circular refs.
 */
function serialize(val, seen = new WeakSet()) {
    return JSON.stringify(val, function (key, value) {
        if (value === undefined)
            return '\u0000__undefined__\u0000';
        if (isInteger(value))
            return `bigint:${value}`;
        if (isFunction(value))
            return `function:${value.name || 'anonymous'}`;
        if (isReference(value)) {
            if (seen.has(value))
                return '<Circular>';
            seen.add(value);
            if (isMap(value))
                return `map:[${Array.from(value.entries()).map(e => serialize(e, seen)).sort().join(',')}]`;
            if (isSet(value))
                return `set:[${Array.from(value).map(v => serialize(v, seen)).sort().join(',')}]`;
        }
        return value;
    });
}
/**
 * Curries a function to allow partial application of its arguments.
 *
 * @param fn - The original function to curry
 * @returns A curried version of the function
 * @example
 * ```ts
 * const add = curry((a: number, b: number) => a + b);
 * add(1)(2); // 3
 * ```
 */
export function curry(fn) {
    return function curried(...args) {
        return (args.length >= fn.length)
            ? fn(...args)
            : (...nextArgs) => curried(...args, ...nextArgs);
    };
}
/**
 * Memoizes a function, caching its return values based on serialized arguments.
 *
 * @param fn - The function to memoize
 * @returns A memoized version of the function
 * @example
 * ```ts
 * const expensive = memoizeFunction((a, b) => a * b);
 * ```
 */
export function memoizeFunction(fn) {
    const cache = new Map(); // using a Map for better key handling than plain objects
    return function (...args) {
        const key = serialize(args);
        if (!cache.has(key)) {
            const result = fn.apply(this, args); // call the original function with the correct context
            cache.set(key, Object.freeze(result)); // stash the result for subsequent calls
        }
        return cache.get(key);
    };
}
const wm = new WeakMap();
/**
 * Manually clears the memoization cache for an object instance.
 *
 * @param obj - The object whose cache should be cleared
 * @example
 * ```ts
 * clearCache(myInstance);
 * ```
 */
export function clearCache(obj) {
    wm.delete(obj);
}
/**
 * Defines a PropertyDescriptor for an object's memoized method.
 * Caches the results of method calls on a per-instance basis.
 *
 * @param name - The name of the method
 * @param fn - The method implementation
 * @returns A PropertyDescriptor containing the memoization logic
 * @example
 * ```ts
 * Object.defineProperty(target, 'calc', memoizeMethod('calc', () => 42));
 * ```
 */
export function memoizeMethod(name, fn) {
    return {
        enumerable: false,
        configurable: false,
        writable: false,
        value: function (...args) {
            const key = `${String(name)},${serialize(args)}`;
            let cache = wm.get(this);
            if (!cache) { // add a new Map into the WeakMap
                cache = new Map();
                wm.set(this, cache);
            }
            if (!cache.has(key)) { // first time for this method
                const result = fn.apply(this, args); // evaluate the method
                secure(result); // freeze the returned value (if object)
                cache.set(key, result); // stash the result
            }
            return cache.get(key);
        }
    };
}
